// MyProjects.jsx
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { Alert, Skeleton, Box, Modal, Typography, Grid, Paper, Container } from '@mui/material';
import { Close, ExpandLess, ExpandMore, CheckCircle, Cancel, HourglassEmpty, AssignmentTurnedIn } from '@mui/icons-material';
import Localbase from 'localbase';

import GetProjects from '../../../actions/course/GetProjects';
import ProjectCard from './ProjectCard'; 
import AssignmentsChart from './AssignmentsChart';
import ProjectSubmit from './ProjectSubmit';
import SetPage from '../../../data/SetPage';
import ModalStyle from '../../../data/ModalStyle';
import noprojects from '../../../assets/images/no-assignments.svg';

// A small component for displaying stats in the report
const StatBox = ({ title, value, icon }) => (
  <Grid item xs={6} sm={3} md={6}>
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
      {icon}
      <Typography variant="h6" fontWeight="bold">{value}</Typography>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
    </Paper>
  </Grid>
);

const MyProjects = () => {
  const { user } = useContext(AuthContext);
  const { course_url, batch_id } = useParams();
  const db = new Localbase("db");

  const [projects, setProjects] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  
  const fetchProjects = useCallback(async () => {
    try {
      const res = await GetProjects({ payload: { user: user.SessionId, course_url, batch_id } });
      if (res&&res.status === "success" && res.data) {
        setProjects(res.data);
        setReport(res.report[0]);
        db.collection("paths").add({ projects: res.data, report: res.report[0] }, `projects-${course_url}-${batch_id}`);
      }
    } catch (error) {
      console.error("Failed to fetch new projects:", error);
    } finally {
      setLoading(false);
    }
  }, [user.SessionId, course_url, batch_id, db]);

  useEffect(() => {
    SetPage("Projects");  
    db.collection("paths").doc(`projects-${course_url}-${batch_id}`).get()
      .then(data => {
        if (data) {
          setProjects(data.projects);
          setReport(data.report);
          setLoading(false);
        } else {
          fetchProjects(); 
        }
      }).catch(() => fetchProjects());
  }, [ course_url, batch_id, db]);

  const handleInitiateSubmission = ({ id }) => {
    setCurrentProjectId(id);
    setSubmitModalOpen(true);
  };

  // This function is passed to the submission modal
  // It refetches data only when a submission is successful
  const handleSubmissionSuccess = () => {
    setSubmitModalOpen(false);
    fetchProjects(); // Refetch data to get the latest status
  };
  
  const renderSkeletons = () => (
    Array.from(new Array(3)).map((_, index) => (
      <Skeleton key={index} variant="rectangular" height={220} sx={{ borderRadius: 4, mb: 3 }} />
    ))
  );

  return (
    <>
      <Modal open={submitModalOpen} onClose={() => setSubmitModalOpen(false)}>
        <Box sx={ModalStyle} className="col-lg-4 p-3 rounded col-md-5 col-sm-6 col-11">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Upload Project</Typography>
            <Close sx={{ cursor: 'pointer' }} onClick={() => setSubmitModalOpen(false)} />
          </Box>
          <hr />
          <ProjectSubmit id={currentProjectId} onUploadSuccess={handleSubmissionSuccess} />
        </Box>
      </Modal>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Left Side: Projects List */}
          <Grid item xs={12} md={7}>
            {loading ? (
              renderSkeletons()
            ) : projects && projects.length > 0 ? (
              projects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onInitiateSubmission={handleInitiateSubmission}
                />
              ))
            ) : (
              <Paper sx={{ textAlign: 'center', p: 4, borderRadius: 4 }}>
                <img src={noprojects} alt="No projects found" style={{ width: '60%', maxWidth: '250px', marginBottom: '1rem' }} />
                <Typography variant="h5" gutterBottom>No Projects Yet</Typography>
                <Typography color="text.secondary">
                  Please contact your trainer for more details.
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* Right Side: Progress Report */}
          <Grid item xs={12} md={5}>
            {loading ? (
              <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 4 }} />
            ) : report && (
              <Paper sx={{ p: 3, borderRadius: 4, border: `#dbcbcb 1px solid`,  }}>
                <Typography  sx={{
            color: "#000",
            letterSpacing: 0.2,
            fontSize: { xs: "1.1rem", sm: "2.25rem" },
          }} variant="h5" fontWeight="bold" gutterBottom>
                  Progress Report
                </Typography>
                <hr />
                {report.pending > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You have {report.pending} pending project(s)!
                  </Alert>
                )}
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <StatBox title="Total" value={report.total_assignments} icon={<AssignmentTurnedIn color="primary" sx={{mb:1}}/>} />
                  <StatBox title="Submitted" value={report.submitted} icon={<CheckCircle color="info" sx={{mb:1}}/>} />
                  <StatBox title="Completed" value={report.completed} icon={<CheckCircle color="success" sx={{mb:1}}/>} />
                  <StatBox title="Rejected" value={report.rejected} icon={<Cancel color="error" sx={{mb:1}}/>} />
                </Grid>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', mb: report.completed > 0 ? 2 : 0 }}>
                    <Typography variant="body1">Marks Earned</Typography>
                    <Typography variant="h5" fontWeight="bold">{report.marks_earned} / {report.total_marks}</Typography>
                </Paper>

                {report.completed > 0 && <AssignmentsChart report={report} />}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default MyProjects;